/**
 * FloatingPlayer — the site-wide dock. Renders nothing until a track is
 * loaded. Pops into the bottom-right corner when playback starts. Desktop:
 * drag anywhere by the handle; mobile: docked to the bottom, swipe down on
 * the handle to minimize. Minimized → round Dolly tile that keeps playing.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, SkipBack, SkipForward, Shuffle, Timer, Heart, Download, Minus, X, Volume2, GripHorizontal, Music, ListMinus, Info, Plus, Move, Lock, RotateCcw, Wand2, AudioWaveform } from "lucide-react";
import { usePlayer } from "./PlayerContext";
import { SavesLivesTagline, AnonymousAccountCTA } from "./PlayerBits";
import { BandsStrip } from "./BandsStrip";
import { ShareTrackButton } from "./ShareTrackButton";
import { KwikiPanel } from "./KwikiPanel";
import { useDragPosition, DraggableTile, tileDefault } from "../hooks/useDragPosition";
import { Waveform } from "./Waveform";
import { usePeaks } from "./usePeaks";
import { SampleSlider } from "./SampleSlider";
import { CuratorPanel, useCuratorMark } from "./CuratorPanel";
import { COLORS, colorHex } from "./sampler";

export const LLAMA_IMG = "https://customer-assets.emergentagent.com/job_211279cf-e69f-4285-b28c-6209ba7e3f92/artifacts/dlad1gog_dolly%20the%20llama%20this%20music%20saves%20lives.png";
const POS_KEY = "wall_player_pos";
const DOCK_W = 372;
const fmt = (s) => { if (!isFinite(s) || isNaN(s)) return "0:00"; const m = Math.floor(s / 60); return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`; };
const bottomRight = (h = 340) => ({ x: Math.max(8, window.innerWidth - DOCK_W - 16), y: Math.max(8, window.innerHeight - h - 16) });

const useDrag = (enabled) => useDragPosition(POS_KEY, bottomRight, enabled);

export const FloatingPlayer = () => {
  const p = usePlayer();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  const [showInfo, setShowInfo] = useState(false);
  const [popping, setPopping] = useState(false);
  const dockRef = useRef(null);
  useEffect(() => { const f = () => setIsMobile(window.innerWidth < 640); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, []);
  const drag = useDrag(!isMobile);
  const swipe = useRef(null);
  const cm = useCuratorMark(p);
  const peaks = usePeaks(p?.current);
  const [pinMode, setPinMode] = useState(false);
  const [curateOpen, setCurateOpen] = useState(() => localStorage.getItem("wall_curate_open") !== "0");
  const curId = p?.current?.item_id;
  useEffect(() => { setPinMode(false); }, [curId]);
  const flipCurate = () => setCurateOpen((v) => { localStorage.setItem("wall_curate_open", v ? "0" : "1"); return !v; });

  // Playback just started → pop the dock into the bottom-right corner.
  useEffect(() => {
    if (!p?.popKey) return;
    setPopping(true);
    const t1 = setTimeout(() => { const h = dockRef.current?.offsetHeight || 340; const np = bottomRight(h); drag.setPos(np); localStorage.setItem(POS_KEY, JSON.stringify(np)); }, 30);
    const t2 = setTimeout(() => setPopping(false), 700);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p?.popKey]);

  // Keep the whole dock on screen when its height changes (info panel, anon CTA, limit note).
  useEffect(() => {
    if (isMobile || !dockRef.current || !("ResizeObserver" in window)) return;
    const ro = new ResizeObserver(() => {
      const h = dockRef.current?.offsetHeight || 0;
      drag.setPos((cur) => {
        const maxY = Math.max(8, window.innerHeight - h - 8);
        const maxX = Math.max(8, window.innerWidth - DOCK_W - 8);
        const nx = Math.min(cur.x, maxX), ny = Math.min(cur.y, maxY);
        return nx === cur.x && ny === cur.y ? cur : { x: nx, y: ny };
      });
    });
    ro.observe(dockRef.current);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, p?.current, p?.minimized]);

  useEffect(() => {
    const root = document.documentElement;
    const set = () => root.style.setProperty("--dock-h", isMobile && p?.current && !p?.minimized ? `${dockRef.current?.offsetHeight || 0}px` : "0px");
    set();
    const id = setInterval(set, 500);
    return () => { clearInterval(id); root.style.setProperty("--dock-h", "0px"); };
  }, [isMobile, p?.current, p?.minimized]);

  if (!p?.current) return null;
  const { current: t, playing, time, duration, limitSeconds, shuffle, sample, minimized, limitHit, lib, previewStart } = p;
  const clipStart = previewStart || 0;
  const clipMode = isFinite(limitSeconds); // previews & samples count from where the clip starts
  const dur = duration || t.length_sec || peaks?.duration || 0;
  const max = clipMode ? Math.max(0, Math.min(limitSeconds, (dur || limitSeconds + clipStart) - clipStart)) : dur;
  const displayTime = clipMode ? Math.max(0, time - clipStart) : time;
  const liked = p.isLiked(t.item_id);
  const color = p.curate && cm.mark ? cm.mark.color : p.trackColor(t);
  const cues = p.curate && cm.mark ? cm.mark.cues : p.trackCues(t);
  const style = isMobile ? {} : { left: drag.pos.x, top: drag.pos.y };

  if (minimized) {
    let tilePos = null;
    try { tilePos = JSON.parse(localStorage.getItem("wall_player_tile_pos")) || tileDefault("left")(); } catch { tilePos = tileDefault("left")(); }
    const vw = window.innerWidth, vh = window.innerHeight;
    const panelStyle = isMobile ? { left: "50%", transform: "translateX(-50%)", bottom: 96 }
      : { left: Math.max(8, Math.min(tilePos.x - 170, vw - 436)), ...(tilePos.y > vh / 2 ? { bottom: Math.max(8, vh - tilePos.y + 12) } : { top: tilePos.y + 84 }) };
    return (
      <>
        <DraggableTile
          posKey="wall_player_tile_pos"
          defaultPos={tileDefault("left")}
          onTap={() => p.setKwiki(!p.kwiki)}
          className="h-[72px] w-[72px] rounded-full shadow-2xl ring-4 ring-[#54E3FF]/80 overflow-hidden bg-[#120A24]"
          ariaLabel={p.kwiki ? "Close Kwiki" : "Open Kwiki — pinned, recent and new songs"}
          testId="player-minimized-tile"
        >
          <img src={LLAMA_IMG} alt="Dolly the llama — this music saves lives" className="h-full w-full object-cover object-top pointer-events-none" draggable={false} />
          {playing && <span className="absolute bottom-1 right-1 h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-[#120A24] animate-pulse" />}
        </DraggableTile>
        {p.kwiki && (
          <div className="fixed z-[69]" style={panelStyle} data-testid="kwiki-anchor">
            <KwikiPanel onClose={() => p.setKwiki(false)} onExpand={() => { p.setKwiki(false); p.setMinimized(false); }} />
          </div>
        )}
      </>
    );
  }

  const onHandleTouchStart = (e) => { swipe.current = e.touches[0].clientY; };
  const onHandleTouchEnd = (e) => { if (swipe.current != null && e.changedTouches[0].clientY - swipe.current > 40) p.setMinimized(true); swipe.current = null; };

  return (
    <div
      ref={dockRef}
      style={{ ...style, width: isMobile ? undefined : DOCK_W }}
      className={`fixed z-[70] text-white bg-[#120A24]/95 backdrop-blur-xl border border-white/10 shadow-2xl overflow-hidden ${isMobile ? "left-0 right-0 bottom-0 rounded-t-2xl" : "rounded-2xl"} ${popping ? "player-pop" : ""}`}
      data-testid="floating-player"
    >
      {p.nowArt && (
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true" data-testid="player-now-art">
          <img key={p.nowArt} src={absUrl(p.nowArt)} alt="" className="h-full w-full object-cover opacity-45 player-art-in" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#120A24]/70 via-[#120A24]/55 to-[#120A24]/90" />
        </div>
      )}
      <div
        className={`relative flex items-center justify-between px-3 pt-2 ${isMobile ? "" : "cursor-grab active:cursor-grabbing"} touch-none select-none`}
        {...(isMobile ? {} : drag.handlers)}
        onTouchStart={onHandleTouchStart}
        onTouchEnd={onHandleTouchEnd}
        data-testid="player-drag-handle"
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300 flex items-center gap-1.5">
          <Music className="h-3 w-3" /> {t.reel ? "SAMPLER" : "ON THE WALL"}
          <button type="button" onClick={() => setShowInfo((v) => !v)} className={`ml-1 h-7 w-7 rounded-full flex items-center justify-center hover:bg-white/10 ${showInfo ? "text-[#54E3FF] bg-white/10" : "text-[#54E3FF]"}`} aria-label="How this player works" title="How this player works" data-testid="player-info-btn"><Info className="h-5 w-5" /></button>
        </span>
        <button
          type="button"
          onClick={() => p.setMinimized(true)}
          className="mx-1 flex-1 min-w-0 flex items-center justify-center gap-1 rounded-full px-1.5 py-1 text-[9px] font-bold uppercase tracking-[0.04em] text-white/60 hover:text-white hover:bg-white/10 whitespace-nowrap"
          title="Minimize to Dolly while you scroll"
          data-testid="player-minimize-text-btn"
        >
          <span className="truncate"><span className="text-amber-300">Minimize</span> while you scroll</span>
        </button>
        <div className="flex items-center gap-1">
          {p.curate && <button type="button" onClick={flipCurate} className={`p-1 rounded hover:bg-white/10 ${curateOpen ? "text-amber-300" : "text-white/60"}`} aria-label="Curator tools" title="Curator tools" data-testid="player-curate-toggle"><Wand2 className="h-4 w-4" /></button>}
          <button type="button" onClick={() => p.setMinimized(true)} className="p-1 rounded hover:bg-white/10" aria-label="Minimize" data-testid="player-minimize-btn"><Minus className="h-4 w-4" /></button>
          <button type="button" onClick={p.stop} className="p-1 rounded hover:bg-white/10" aria-label="Close player" data-testid="player-close-btn"><X className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="relative">
      {showInfo ? (
        <InfoPanel access={lib.access} onClose={() => setShowInfo(false)} />
      ) : (
        <>
          <div className="px-4 pt-2 flex items-center gap-3">
            <div className="h-14 w-14 rounded-lg overflow-hidden bg-white/10 shrink-0">
              {t.album_art_url ? <img src={absUrl(t.album_art_url)} alt="" className="h-full w-full object-cover" /> : <Music className="h-6 w-6 m-4 text-white/50" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-bold leading-tight line-clamp-2" data-testid="player-track-title">{t.title}</p>
              <p className="text-sm text-white/70 truncate">
                {t.artist_slug ? <Link to={`/artists/${t.artist_slug}`} className="hover:text-amber-300" data-testid="player-artist-link">{t.artist_name}</Link> : t.artist_name}
              </p>
              <div className="flex gap-1 mt-0.5 flex-wrap">
                {t.reel && <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-300/20 text-amber-200 rounded px-1.5 max-w-[150px] truncate" data-testid="player-reel-source">{t.src_name || "Sampler"}</span>}
                {p.curate && color && <span className="text-[10px] font-black uppercase tracking-wider rounded px-1.5 text-[#120A24]" style={{ backgroundColor: colorHex(color) }} title={COLORS[color]?.hint} data-testid="player-color-badge">{COLORS[color]?.label}</span>}
                {sample && <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-400 text-amber-950 rounded px-1.5">{p.sampleSeconds === Infinity ? "full songs · random" : `${p.sampleSeconds}s sample`}</span>}
                {!sample && lib.access === "anonymous" && !t.volt && (p.grant
                  ? <span className="text-[10px] font-bold uppercase tracking-wider bg-[#54E3FF] text-[#120A24] rounded px-1.5" data-testid="player-grant-badge" title="A friend sent you this track — full plays on them">shared · {p.grant.left} {p.grant.left === 1 ? "play" : "plays"} left</span>
                  : <span className="text-[10px] font-bold uppercase tracking-wider bg-white/15 rounded px-1.5">30s preview</span>)}
                {t.volt ? <span className="text-[10px] font-bold uppercase tracking-wider bg-[#54E3FF]/20 text-[#54E3FF] rounded px-1.5" data-testid="player-volt-badge" title="A live tape from THE VÖLT — free to stream, never for sale">THE VÖLT · live tape</span>
                  : t.stream_only && <span className="text-[10px] font-bold uppercase tracking-wider bg-sky-400/20 text-sky-200 rounded px-1.5">stream only</span>}
              </div>
            </div>
            <Link to="/passport" className="block h-14 w-20 rounded-lg overflow-hidden ring-2 ring-[#54E3FF] shrink-0" title="Dolly the llama — this music saves lives" data-testid="player-dolly">
              <img src={LLAMA_IMG} alt="Dolly the llama" className="h-full w-full object-cover object-top" />
            </Link>
            {!t.reel && (
              <>
                <button type="button" onClick={() => p.toggleLike(t)} className={`p-2 rounded-full hover:bg-white/10 ${liked ? "text-rose-400" : "text-white/70"}`} aria-label="Add to playlist" data-testid="player-like-btn">
                  <Heart className={`h-5 w-5 ${liked ? "fill-current" : ""}`} />
                </button>
                <ShareTrackButton track={t} className="p-2 rounded-full hover:bg-white/10 text-white/70 hover:text-[#54E3FF]" />
              </>
            )}
          </div>

          <div className="px-4 pt-2">
            {peaks ? (
              <Waveform
                peaks={peaks.peaks} duration={dur} time={time} color={colorHex(color)} cues={cues}
                clip={clipMode ? [clipStart, clipStart + Math.min(limitSeconds, dur || limitSeconds)] : null}
                pinMode={p.curate && pinMode} onSeek={p.seek} onCue={p.seek} onAddCue={cm.addCue}
              />
            ) : (
              <input
                type="range" min={0} max={max || 0} step={0.1} value={Math.min(displayTime, max || 0)}
                onChange={(e) => p.seek(parseFloat(e.target.value) + (clipMode ? clipStart : 0))}
                className="w-full accent-amber-400 h-1.5 cursor-pointer"
                aria-label="Seek" data-testid="player-seek"
              />
            )}
            <div className="flex justify-between text-[11px] text-white/60 -mt-0.5">
              <span>{fmt(displayTime)}</span>
              <span>{fmt(max)}</span>
            </div>
            {lib.access === "anonymous" && !t.volt && (
              <div className="mt-1.5" data-testid="player-anon-wrap">
                {limitHit && !sample && <p className="text-xs text-amber-200 mb-1" data-testid="player-limit-note">Preview ended — the full track is one tap away:</p>}
                <AnonymousAccountCTA compact />
              </div>
            )}
          </div>

          <div className="px-3 pb-2 pt-1 flex items-center justify-between">
            <button type="button" onClick={p.toggleShuffle} className={`p-2 rounded-full hover:bg-white/10 ${shuffle ? "text-amber-300" : "text-white/60"}`} aria-label="Shuffle" title="Random play — no repeats until every track has played" data-testid="player-shuffle-btn"><Shuffle className="h-4 w-4" /></button>
            <button type="button" onClick={() => p.rewind(10)} className="p-2 rounded-full hover:bg-white/10 text-white/80" aria-label="Back 10 seconds" title="Back 10 seconds" data-testid="player-rewind-btn"><RotateCcw className="h-4 w-4" /></button>
            <button type="button" onClick={p.prev} className="p-2 rounded-full hover:bg-white/10" aria-label="Previous" data-testid="player-prev-btn"><SkipBack className="h-5 w-5" /></button>
            <button type="button" onClick={p.toggle} className="h-12 w-12 rounded-full bg-amber-400 text-amber-950 flex items-center justify-center hover:bg-amber-300 transition-colors" aria-label={playing ? "Pause" : "Play"} data-testid="player-play-btn">
              {playing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 ml-0.5" />}
            </button>
            <button type="button" onClick={p.next} className="p-2 rounded-full hover:bg-white/10" aria-label="Next" data-testid="player-next-btn"><SkipForward className="h-5 w-5" /></button>
            <button type="button" onClick={p.toggleSample} className={`p-2 rounded-full hover:bg-white/10 ${sample ? "text-amber-300" : "text-white/60"}`} aria-label="Sample mode" title="Sample mode — a few seconds of each track, random order, no repeats (pick the length below)" data-testid="player-sample-btn"><Timer className="h-4 w-4" /></button>
          </div>

          {sample && <SampleSlider value={p.sampleSeconds} onChange={p.setSampleSeconds} />}
          <div className="px-4 pb-2 flex items-center gap-2 text-white/60">
            <Volume2 className="h-4 w-4" />
            <input type="range" min={0} max={1} step={0.01} value={p.volume} onChange={(e) => p.setVolume(parseFloat(e.target.value))} className="flex-1 accent-amber-400 h-1 cursor-pointer" aria-label="Volume" data-testid="player-volume" />
            {!t.reel && <button type="button" onClick={() => p.toggleRemoved(t)} className="p-1.5 rounded hover:bg-white/10 text-white/60" title="Remove this song from your player (skips to the next)" aria-label="Remove from player" data-testid="player-remove-btn"><ListMinus className="h-4 w-4" /></button>}
            {lib.access === "passport" && !t.stream_only && (
              <button type="button" onClick={() => p.download(t)} className={`p-1.5 rounded hover:bg-white/10 ${liked ? "text-white" : "text-white/40"}`} title={liked ? "Download this track" : "♥ first — downloads come from Your Playlist"} aria-label="Download" data-testid="player-download-btn"><Download className="h-4 w-4" /></button>
            )}
          </div>
          {p.curate && curateOpen && <CuratorPanel cm={cm} pinMode={pinMode} setPinMode={setPinMode} />}
        </>
      )}

      <BandsStrip compact />
      <SavesLivesTagline />
      </div>
    </div>
  );
};

const InfoPanel = ({ access, onClose }) => {
  const Row = ({ icon: Icon, title, text }) => (
    <li className="flex gap-2.5">
      <span className="h-7 w-7 rounded-full bg-white/10 flex items-center justify-center shrink-0 mt-0.5"><Icon className="h-3.5 w-3.5 text-amber-300" /></span>
      <span className="text-xs leading-snug"><strong className="text-white">{title}</strong> <span className="text-white/70">{text}</span></span>
    </li>
  );
  return (
    <div className="px-4 pt-2 pb-3 space-y-3 max-h-[60vh] overflow-y-auto" data-testid="player-info-panel">
      <p className="text-sm font-extrabold text-[#54E3FF]">How this player works</p>
      <ul className="space-y-2">
        <Row icon={Play} title="Play / pause, ⏮ ⏭." text="Every song in the HOARD is in your player. It keeps playing while you browse THE WALL." />
        <Row icon={Shuffle} title="Random (on by default)." text="No repeats until every track has played. Tap to play in order instead." />
        <Row icon={Timer} title="Sample mode." text="A taste of every song — slide to any length (22 seconds to start) or full songs — random, no repeats. Speed-date the whole HOARD." />
        <Row icon={AudioWaveform} title="The waveform." text="Tap anywhere on it to jump there. The white marks are the best places to start — songs begin at the ★ one." />
        <Row icon={RotateCcw} title="Back 10." text="Missed something? Tap ⟲ to hear the last 10 seconds again." />
        <Row icon={GripHorizontal} title="Minimize while you scroll" text="tap the header text (or −) to tuck the player into Dolly; tap her to bring it back." />
        <Row icon={ListMinus} title="− drops a song" text="from your player; + on the HOARD page brings it back." />
        <Row icon={Heart} title="♥ = Your Playlist." text="Your download list. It's saved to your account and follows you to every device." />
        <Row icon={Download} title="Downloads" text="are playlist-only, up to 22 a day, and open once your HOARD is unlocked (a verified $20+ / $5+ sliding-scale gift to a local 501(c)(3)). Some tracks may be stream-only." />
        <Row icon={Move} title="Move it." text="Drag the top bar anywhere (desktop). − minimizes to Dolly; tap her to come back." />
      </ul>
      {access === "anonymous" && <AnonymousAccountCTA compact />}
      {access === "member" && (
        <Link to="/passport" className="flex items-center gap-2 rounded-xl border border-amber-300/50 bg-amber-400/10 hover:bg-amber-400/20 px-3 py-2 text-xs" data-testid="player-info-passport-link">
          <Lock className="h-4 w-4 text-amber-300 shrink-0" /><span><strong className="text-white">Unlock downloads:</strong> <span className="text-white/75">unlock your HOARD with a donation receipt.</span></span>
        </Link>
      )}
      <button type="button" onClick={onClose} className="w-full rounded-full bg-white/10 hover:bg-white/15 py-1.5 text-xs font-bold" data-testid="player-info-close">Got it</button>
    </div>
  );
};

export const absUrl = (u) => (!u || u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
